/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";

/**
 * This component returns a dialog that will call a callback function with
 * either true or false depending on a users input.
 * @param {String} title The title of the dialog.
 * @param {String} message The message contained in  the dialog.
 * @param {Boolean} confirmOpen A boolean specifying whether or not the dialog is open.
 * @param {(Boolean) => null} onClose The callback function to be called with either true or false when the user interacts with the dialog.
 */
export const ConfirmDialog = ({ title, message, confirmOpen, onClose }) => {
	const [disabled, setDisabled] = useState(false);

	const handleNo = async () => {
		try {
			setDisabled(true);
			await onClose(false);
		} catch (_) {}
		setDisabled(false);
	};

	const handleYes = async () => {
		try {
			setDisabled(true);
			await onClose(true);
		} catch (_) {}
		setDisabled(false);
	};

	return (
		<Dialog open={confirmOpen} aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-description">
			<DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
			<DialogContent>
				<DialogContentText id="confirm-dialog-description">{message}</DialogContentText>
			</DialogContent>
			<DialogActions>
				<button
					className="btn btn-outline-secondary"
					type="button"
					disabled={disabled}
					onClick={handleNo}
					color="primary"
					data-test-id="confirm-dialog-no-btn"
				>
					No
				</button>

				<button
					className="btn btn-primary"
					type="button"
					disabled={disabled}
					onClick={handleYes}
					color="secondary"
					data-test-id="confirm-dialog-yes-btn"
				>
					Yes
				</button>
			</DialogActions>
		</Dialog>
	);
};

ConfirmDialog.propTypes = {
	confirmOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	title: PropTypes.string.isRequired,
	message: PropTypes.string.isRequired,
};
